import { Spinner } from "@design";
import { type ReactNode, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ContextInfoData, DocumentLineageResponse, SrcSchemasChatMessageResponse } from "@/api";
import { MessageBubble } from "@/components/chat/MessageBubble";
import { Text } from "@/components/Text";
import type { StreamStatus } from "@/streaming/useOperationStream";
import type { AskUserPrompt } from "./AskUserCard";
import { SavedMessage, ThreadColumn } from "./ChatMessages";
import type { ConnectorConnectRequest, ExtensionAccessRequest } from "./chatStreamReducer";
import { documentsByMessage, type OpenDocument } from "./documents/documentInfo";
import { DraftCards } from "./drafts/DraftCards";
import type { SavedDraft } from "./drafts/draftModel";
import { type SentAttachment, SentAttachments } from "./MessageAttachments";
import { CONTINUE_PROMPT, ContinuedChip, isContinuation, PausedNotice, pausedLastReply } from "./PausedNotice";
import { StreamingAssistantMessage } from "./StreamingAssistantMessage";
import { useStickToBottom } from "./useStickToBottom";

/** A sent turn whose reply is streaming. `baseCount` saved messages precede it. */
export interface PendingTurn {
  sessionUuid: string;
  /** The user's message. Unset for a reply picked up before its message was saved, or one answering a saved one. */
  prompt?: string;
  /** The files sent with `prompt`. */
  attachments?: SentAttachment[];
  opId: string;
  baseCount: number;
  /** The reply ended without completing (failed/stopped) and is kept on screen. */
  settled?: boolean;
  /** Stop was pressed: the reply asks the backend to stop, and ends here if that isn't confirmed in time. */
  stopRequested?: boolean;
  /** A Continue turn (MHS-860): shown as a chip, not the prompt. */
  continuation?: boolean;
  /** A proposal's choice (MHS-795), e.g. "Start research": the card's answer, not a message to send again. */
  decision?: boolean;
  /** When the reply started (ISO), for a research run's elapsed time. */
  startedAt?: string;
}

function UserTurn({
  prompt,
  files = [],
  author,
}: Readonly<{ prompt: string; files?: SentAttachment[]; author: string }>) {
  return (
    <div className="flex flex-col items-end gap-1.5">
      <MessageBubble variant="user" author={author}>
        {prompt}
      </MessageBubble>
      <SentAttachments files={files} />
    </div>
  );
}

export function ChatThread({
  loading,
  messages,
  myUuid,
  agentName,
  documents = [],
  onOpenDocument,
  pending,
  optimisticPrompt,
  optimisticAttachments,
  onSettled,
  onAskUser,
  onExtensionAccess,
  onConnectorConnect,
  onContext,
  after,
  emptyState,
  drafts = [],
  onContinue,
  continueDisabled = false,
  optimisticContinuation = false,
}: Readonly<{
  loading: boolean;
  messages: SrcSchemasChatMessageResponse[];
  myUuid?: string;
  agentName?: string | null;
  /** The chat's documents; each reply shows a card for the ones it created. */
  documents?: DocumentLineageResponse[];
  onOpenDocument?: (open: OpenDocument) => void;
  pending: PendingTurn | null;
  /** The prompt being sent, before the reply starts. */
  optimisticPrompt?: string;
  optimisticAttachments?: SentAttachment[];
  /** The live reply ended; `retry` when it was stopped to send its message again. */
  onSettled: (status: StreamStatus, retry: boolean) => void;
  onAskUser: (prompt: AskUserPrompt) => void;
  onExtensionAccess?: (request: ExtensionAccessRequest) => void;
  onConnectorConnect?: (request: ConnectorConnectRequest) => void;
  onContext?: (info: ContextInfoData) => void;
  /** Rendered after the last turn (e.g. the assistant's question card). */
  after?: ReactNode;
  /** Replaces the default empty state of a new chat (Build with AI's example prompts). */
  emptyState?: ReactNode;
  /** The chat's saved Build with AI drafts; each shows under the reply that proposed it. */
  drafts?: SavedDraft[];
  onContinue?: () => void;
  continueDisabled?: boolean;
  optimisticContinuation?: boolean;
}>) {
  const { t } = useTranslation("chat");
  const { scrollRef, contentRef, toBottom } = useStickToBottom<HTMLElement, HTMLDivElement>();
  // Sending (or answering a question card) always brings the conversation into view,
  // even if I'd scrolled up to read; the reply then stays pinned as it streams.
  useEffect(() => {
    if (optimisticPrompt) toBottom();
  }, [optimisticPrompt, toBottom]);
  // While a reply streams, saved messages past the send point (the new user turn,
  // partial saves) are hidden so the live turn isn't shown twice.
  const saved = pending ? messages.slice(0, pending.baseCount) : messages;
  const docsByMessage = useMemo(() => documentsByMessage(documents), [documents]);
  const draftsByMessage = useMemo(() => draftsByReply(drafts), [drafts]);
  // Drafts after the last reply with text; while a reply streams they belong to it and show there.
  const trailing = pending ? [] : (draftsByMessage.get(null) ?? []);
  const empty = !loading && saved.length === 0 && !pending && !optimisticPrompt;
  const paused = pending || optimisticPrompt ? null : pausedLastReply(saved);
  // A Continue turn shows as a chip, not as its prompt (MHS-860), live and saved alike. Live, the turn's own
  // continuation flag decides: a message the user typed with the same words is an ordinary message.
  const sendingContinue = optimisticContinuation && optimisticPrompt === CONTINUE_PROMPT;
  const continuing = Boolean(pending?.continuation) || (optimisticContinuation && !sendingContinue);

  return (
    <section
      ref={scrollRef}
      aria-label={t("thread.label")}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be keyboard-reachable (WCAG 2.1.1, axe scrollable-region-focusable); a conversation can hold no focusable content.
      tabIndex={0} // NOSONAR
      className="min-h-0 flex-1 overflow-y-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-border-focus"
    >
      <div ref={contentRef}>
        <ThreadColumn>
          {loading && (
            <div className="flex justify-center py-12">
              <Spinner size="md" aria-label={t("thread.loading")} />
            </div>
          )}
          {empty &&
            (emptyState ?? (
              <div className="py-16 text-center">
                <Text variant="h2" as="h1">
                  {t("thread.emptyTitle")}
                </Text>
                <Text tone="secondary" className="mt-2">
                  {t("thread.emptySubtitle")}
                </Text>
              </div>
            ))}
          {saved.map((message) =>
            isContinuation(message) ? (
              <ContinuedChip key={message.uuid} />
            ) : (
              <SavedMessage
                key={message.uuid}
                message={message}
                myUuid={myUuid}
                agentName={agentName}
                documents={docsByMessage.get(message.uuid)}
                onOpenDocument={onOpenDocument}
                drafts={draftsByMessage.get(message.uuid)}
              />
            ),
          )}
          {paused && <PausedNotice pause={paused} onContinue={onContinue} disabled={continueDisabled} />}
          {trailing.length > 0 && (
            <div className="ml-10">
              <DraftCards drafts={trailing} />
            </div>
          )}
          {continuing && <ContinuedChip />}
          {pending?.prompt && !continuing && (
            <UserTurn prompt={pending.prompt} files={pending.attachments} author={t("message.you")} />
          )}
          {pending && (
            <StreamingAssistantMessage
              key={pending.opId}
              opId={pending.opId}
              name={agentName}
              startedAt={pending.startedAt}
              stopRequested={pending.stopRequested}
              // Neither a Continue nor a proposal's choice can be sent again as a plain message.
              retryable={Boolean(pending.prompt) && !pending.continuation && !pending.decision}
              onSettled={onSettled}
              onAskUser={onAskUser}
              onExtensionAccess={onExtensionAccess}
              onConnectorConnect={onConnectorConnect}
              onContext={onContext}
              onContinue={onContinue}
              continueDisabled={continueDisabled}
            />
          )}
          {/* The message being sent starts the next turn: after a reply that failed or was stopped, not above it. */}
          {sendingContinue && <ContinuedChip />}
          {optimisticPrompt && !sendingContinue && (
            <UserTurn prompt={optimisticPrompt} files={optimisticAttachments} author={t("message.you")} />
          )}
          {after}
        </ThreadColumn>
      </div>
    </section>
  );
}

function draftsByReply(drafts: readonly SavedDraft[]) {
  const byReply = new Map<string | null, SavedDraft[]>();
  for (const d of drafts) byReply.set(d.messageUuid, [...(byReply.get(d.messageUuid) ?? []), d]);
  return byReply;
}

export function ChatNotFound() {
  const { t } = useTranslation("chat", { keyPrefix: "thread" });
  return (
    <ThreadColumn>
      <Text variant="h2" as="h1">
        {t("notFoundTitle")}
      </Text>
      <Text tone="secondary">{t("notFoundSubtitle")}</Text>
    </ThreadColumn>
  );
}
